import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { membershipApi } from '../lib/api/membership.api';
import { useTenantStore } from '../stores/tenant.store';

/** Query keys scoped by tenant. */
const keys = {
  profile: (t: string) => ['membership', t, 'profile'] as const,
  readiness: (t: string) => ['membership', t, 'readiness'] as const,
  statuses: (t: string) => ['membership', t, 'statuses'] as const,
  evidence: (t: string) => ['membership', t, 'evidence'] as const,
  attestations: (t: string, cycle: number) => ['membership', t, 'attestations', cycle] as const,
};

export function useMembershipProfile() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: keys.profile(tenantId ?? ''),
    queryFn: () => membershipApi.getProfile({ tenantId: tenantId! }),
    enabled: !!tenantId,
  });
}

export function useMembershipReadiness() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: keys.readiness(tenantId ?? ''),
    queryFn: () => membershipApi.getReadiness({ tenantId: tenantId! }),
    enabled: !!tenantId,
  });
}

export function useCriterionStatuses() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: keys.statuses(tenantId ?? ''),
    queryFn: () => membershipApi.listCriterionStatuses({ tenantId: tenantId! }),
    enabled: !!tenantId,
  });
}

export function useMembershipEvidence() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: keys.evidence(tenantId ?? ''),
    queryFn: () => membershipApi.listEvidence({ tenantId: tenantId! }),
    enabled: !!tenantId,
  });
}

export function useDoctrinalAttestations(cycleYear: number, enabled = true) {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  return useQuery({
    queryKey: keys.attestations(tenantId ?? '', cycleYear),
    queryFn: () => membershipApi.listAttestations({ tenantId: tenantId!, cycleYear }),
    // The roster list is admin/registrar-only (the callable enforces it); a
    // faculty member reads only their OWN attestation (self view in the page).
    enabled: !!tenantId && enabled,
  });
}

export function useMembershipMutations() {
  const tenantId = useTenantStore((s) => s.currentTenantId)!;
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['membership', tenantId] });
  };

  const setStage = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.setStage>[0]) => membershipApi.setStage(v),
    onSuccess: invalidate,
  });
  const setCriterionStatus = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.setCriterionStatus>[0]) =>
      membershipApi.setCriterionStatus(v),
    onSuccess: invalidate,
  });
  const upsertEvidence = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.upsertEvidence>[0]) =>
      membershipApi.upsertEvidence(v),
    onSuccess: invalidate,
  });
  const deleteEvidence = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.deleteEvidence>[0]) =>
      membershipApi.deleteEvidence(v),
    onSuccess: invalidate,
  });
  const recordAttestation = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.recordAttestation>[0]) =>
      membershipApi.recordAttestation(v),
    onSuccess: invalidate,
  });
  const runReadiness = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.runReadiness>[0]) =>
      membershipApi.runReadiness(v),
    onSuccess: invalidate,
  });
  // Packet + annex are on-demand artifacts (no persistence) → no invalidation.
  const assemblePacket = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.assemblePacket>[0]) =>
      membershipApi.assemblePacket(v),
  });
  const buildRosterAnnex = useMutation({
    mutationFn: (v: Parameters<typeof membershipApi.buildRosterAnnex>[0]) =>
      membershipApi.buildRosterAnnex(v),
  });

  return {
    tenantId,
    setStage,
    setCriterionStatus,
    upsertEvidence,
    deleteEvidence,
    recordAttestation,
    runReadiness,
    assemblePacket,
    buildRosterAnnex,
  };
}
